import FeatureAction from './FeatureAction';
import React, { useRef, useState } from 'react';
import type { SegmentData } from '../services/segmentCache';
import { mediaDownloadService } from '../services/mediaDownloadService';
import { pickDurableMediaUri } from '../utils/mediaSources';
import { downloadJsonFile, getMediaFilenameExtension, sanitizeDownloadFilenamePart } from '../utils/download';
import { buildSegmentMetadataExport } from '../utils/studioMetadataExport';
import SegmentDownloadButtons from './SegmentDownloadButtons';

/** Shared segment exports for Studio and Search, using the existing media grant path. */
const SegmentDownloadActions: React.FC<{
  segment: SegmentData;
  segmentIndex: number;
  videoId: string;
  videoTitle: string;
  runId?: string | null;
  promptName?: string | null;
  onError: (message: string) => void;
}> = ({ segment, segmentIndex, videoId, videoTitle, runId, promptName, onError }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadInFlightRef = useRef(false);
  const mediaUri = pickDurableMediaUri(segment.gcs_uri, segment.segment_uri);
  const ordinal = segmentIndex + 1;
  const filename = `${sanitizeDownloadFilenamePart(videoTitle, 'video', 30)}_segment_${ordinal}`;

  const downloadMedia = async () => {
    if (!mediaUri?.startsWith('gs://') || downloadInFlightRef.current) return;
    downloadInFlightRef.current = true;
    setIsDownloading(true);
    try {
      await mediaDownloadService.downloadOwnedSegment({
        gcsUri: mediaUri,
        videoId,
        segmentId: segment.segment_id,
        filename: `${filename}.${getMediaFilenameExtension(mediaUri)}`,
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      onError(typeof detail === 'string' ? detail : error?.message || 'Unable to download this segment.');
    } finally {
      downloadInFlightRef.current = false;
      setIsDownloading(false);
    }
  };

  const downloadMetadata = () => {
    downloadJsonFile(buildSegmentMetadataExport({
      segment, segmentIndex, videoId, videoTitle, runId, promptName,
    }), `${filename}_metadata.json`);
  };

  return <SegmentDownloadButtons
    renderAction={(button) => <FeatureAction feature="export_lifecycle">{button}</FeatureAction>}
    onDownloadMedia={downloadMedia}
    onDownloadMetadata={downloadMetadata}
    mediaAvailable={Boolean(mediaUri?.startsWith('gs://'))}
    isDownloading={isDownloading}
  />;
};

export default SegmentDownloadActions;
